/**
 * GameScene - Core gameplay scene with scrolling track
 *
 * Features:
 * - Vertical scrolling background with parallax lines
 * - Track lane area for player movement
 * - Configurable scroll speed based on level
 * - Collision groups for entities
 * - Pause functionality
 */

import Phaser from 'phaser';
import {
  GAME_WIDTH,
  GAME_HEIGHT,
  DURATIONS,
  FONTS,
  SAFE_AREA,
} from '../config';
import {
  SCROLL_SPEED,
  EFFECTS,
  COLLISION_CATEGORY,
  WeaponType,
  GAMEPLAY_START,
  getBaseHpForLevel,
} from '../utils/constants';
import { Button } from '../ui/Button';
import { HUD } from '../ui/HUD';
import { PauseMenu } from '../ui/PauseMenu';
import { Player } from '../entities/Player';
import { Enemy } from '../entities/Enemy';
import { EliteEnemy } from '../entities/EliteEnemy';
import { Gate } from '../entities/Gate';
import { WeaponBox } from '../entities/WeaponBox';
import { InputSystem } from '../systems/InputSystem';
import { WeaponSystem } from '../systems/WeaponSystem';
import { CollisionSystem } from '../systems/CollisionSystem';
import { SpawnSystem } from '../systems/SpawnSystem';
import { BaseSystem } from '../systems/BaseSystem';
import { calculateRunPerformance } from '../systems/RunPerformance';
import { scoreSystem } from '../systems/ScoreSystem';
import { audioSystem } from '../systems/AudioSystem';
import { initParticlePool, destroyParticlePool } from '../systems/ParticlePool';
import { PerformanceMonitor } from '../utils/PerformanceMonitor';
import { logSceneEvent } from '../utils/automation';
import { ResultsSceneData } from './ResultsScene';
import {
  trackLevelStart,
  trackLevelComplete,
  trackLevelFail,
  trackGateChoice,
} from '../utils/analytics';
import { fitImageContain, getWeaponDisplayName, getWeaponGunTextureKey } from '../ui/weaponVisuals';
import { GateOperation } from '../levels/LevelGenerator';
import {
  clampToTrackBounds,
  createGameplayBackground,
  createGameplayTrack,
  createParallaxLines,
  createPauseIconButton,
  setPauseButtonIcon,
  updateParallaxLines,
} from './shared/gameplayScaffold';
import {
  DeterministicHookBindings,
  installDeterministicHooks,
  uninstallDeterministicHooks,
} from './shared/deterministicHooks';
import { destroyIfPresent } from './shared/shutdownHelpers';

declare global {
  interface Window {
    render_game_to_text?: () => string;
    advanceTime?: (ms: number) => void;
  }
}

// Re-export for convenience
export { COLLISION_CATEGORY };

export interface GameSceneData {
  level: number;
  tier?: number;
}

export class GameScene extends Phaser.Scene {
  private static readonly DETERMINISTIC_STEP_MS = 1000 / 60;

  // Scene state
  private isPaused: boolean = false;
  private scrollSpeed: number = SCROLL_SPEED.base;
  private currentLevel: number = 1;
  private currentTier: number = 1;
  private deterministicMode: boolean = false;
  private deterministicTimeMs: number = 0;
  private manualStepInProgress: boolean = false;
  private isResolved: boolean = false;
  private hasShutdown: boolean = false;
  private failReason: 'army' | 'base' | null = null;
  private deterministicHooks: DeterministicHookBindings | null = null;

  // Background elements
  private parallaxLines: Phaser.GameObjects.Line[] = [];

  // Player
  private player!: Player;
  private baseSystem!: BaseSystem;

  // Input
  private inputSystem!: InputSystem;

  // Combat
  private weaponSystem!: WeaponSystem;
  private collisionSystem!: CollisionSystem;
  private spawnSystem!: SpawnSystem;

  // Score tracking
  private score: number = 0;
  private shotsFired: number = 0;
  private shotsHit: number = 0;
  private enemyCollisions: number = 0;

  // UI elements
  private hud!: HUD;
  private pauseButton!: Button;
  private pauseMenu!: PauseMenu;
  private performanceMonitor!: PerformanceMonitor;

  // Track boundaries
  private trackLeftBound: number = 0;
  private trackRightBound: number = GAME_WIDTH;
  private trackPadding: number = 20; // Padding from screen edges

  constructor() {
    super({ key: 'GameScene' });
  }

  init(data: GameSceneData): void {
    this.currentLevel = data.level || 1;
    this.currentTier = data.tier || 1;
    this.isPaused = false;
    this.isResolved = false;
    this.hasShutdown = false;
    this.failReason = null;
    this.score = 0;
    this.shotsFired = 0;
    this.shotsHit = 0;
    this.enemyCollisions = 0;

    // Calculate scroll speed based on level and tier
    this.calculateScrollSpeed();

    // Initialize score system for this level
    scoreSystem.setLevel(this.currentLevel, this.currentTier);
  }

  create(): void {
    logSceneEvent('GameScene');
    // Fade in
    this.cameras.main.fadeIn(DURATIONS.screenTransition, 0, 0, 0);

    // Track level start for analytics
    trackLevelStart(this.currentLevel, this.currentTier);

    // Start gameplay music
    audioSystem.playMusic('gameplay');

    // Initialize particle pool for optimized particle effects
    initParticlePool(this);

    // Create layers in order (back to front)
    this.createBackground();
    this.createTrack();
    this.createParallaxLines();
    this.createPauseButton();
    this.createPauseMenu();

    // Set up collision groups
    this.setupCollisionGroups();

    // Calculate track boundaries
    this.trackLeftBound = this.trackPadding;
    this.trackRightBound = GAME_WIDTH - this.trackPadding;

    // Create player army
    this.player = new Player(this, {
      startingSoldiers: GAMEPLAY_START.startingSoldiers,
      trackWidth: this.trackRightBound - this.trackLeftBound,
      trackPadding: this.trackPadding,
    });
    this.baseSystem = new BaseSystem(getBaseHpForLevel(this.currentLevel, this.currentTier));

    // Create HUD
    this.hud = new HUD(this, {
      soldierCount: this.player.getSoldierCount(),
      score: 0,
      weapon: WeaponType.Pistol,
      baseHp: this.baseSystem.getHp(),
      level: this.currentLevel,
    });

    // Create input system for player movement
    this.inputSystem = new InputSystem(this, {
      player: this.player,
      clampToTrack: this.clampToTrack.bind(this),
      getIsPaused: this.getIsPaused.bind(this),
    });

    // Create weapon system
    this.weaponSystem = new WeaponSystem(this, {
      player: this.player,
      getEnemies: this.getEnemies.bind(this),
      getWeaponBoxes: () => this.spawnSystem?.getActiveWeaponBoxes() ?? [],
      getIsPaused: this.getIsPaused.bind(this),
      onShotFired: () => {
        this.shotsFired += 1;
      },
      onShotHit: () => {
        this.shotsHit += 1;
      },
    });

    // Create collision system
    this.collisionSystem = new CollisionSystem(this, {
      player: this.player,
      getEnemies: this.getEnemies.bind(this),
      getIsPaused: this.getIsPaused.bind(this),
      onEnemyCollision: () => {
        this.enemyCollisions += 1;
      },
      onGameOver: () => this.handleGameOver('army'),
    });

    // Create spawn system for level-based spawning
    this.spawnSystem = new SpawnSystem({
      scene: this,
      level: this.currentLevel,
      tier: this.currentTier,
      getScrollSpeed: this.getScrollSpeed.bind(this),
      getIsPaused: this.getIsPaused.bind(this),
      getCurrentArmySize: () => this.player?.getSoldierCount() ?? GAMEPLAY_START.startingSoldiers,
      trackWidth: this.trackRightBound - this.trackLeftBound,
      trackPadding: this.trackPadding,
    });

    // Set up spawn system event listeners
    this.setupSpawnSystemEvents();

    // Create performance monitor (dev only, toggle with 'P' key)
    this.performanceMonitor = new PerformanceMonitor(this);
    this.performanceMonitor.setPoolStatsProvider(() => ({
      bulletPool: this.weaponSystem.getBulletPoolStats(),
      spatialGrid: this.weaponSystem.getGridStats(),
    }));
    this.performanceMonitor.setEnemyCountProvider(() => this.getEnemies().length);

    this.installAutomationHooks();

    // Ensure lifecycle cleanup always runs when this scene stops/restarts.
    this.events.once(Phaser.Scenes.Events.SHUTDOWN, this.shutdown, this);

    // Start the game loop (defensively de-duplicate on repeated create calls).
    this.events.off('update', this.gameLoop, this);
    this.events.on('update', this.gameLoop, this);
  }

  /**
   * Sets up event listeners for the spawn system
   */
  private setupSpawnSystemEvents(): void {
    const events = this.spawnSystem.getEvents();

    // Handle enemy kills - award points
    events.on('enemyKilled', (_enemy: Enemy, points: number) => {
      scoreSystem.addKillPoints(points);
      this.score = scoreSystem.getCurrentScore();
      this.hud.updateScore(this.score);

      // Extra punch for elite kills
      if (_enemy instanceof EliteEnemy) {
        const { duration, intensity } = EFFECTS.eliteDeathShake;
        this.cameras.main.shake(duration, intensity / 1000);
      }
    });

    // Handle gate activation
    events.on(
      'gateActivated',
      (gate: Gate, operation: GateOperation, scoreFactor: number, soldiersDelta: number, rowIndex: number) => {
      // Track gate choice for analytics
        trackGateChoice(this.currentLevel, rowIndex, Number(scoreFactor.toFixed(2)));

      // Add gate bonus to score
        scoreSystem.addGateBonus(scoreFactor, Math.max(0, soldiersDelta));
        this.score = scoreSystem.getCurrentScore();
        this.hud.updateScore(this.score);
        this.hud.updateSoldierCount(this.player.getSoldierCount() + soldiersDelta);

      // Play soldier multiply cascade sound
        if (soldiersDelta > 0) {
          const soundCount = Math.min(Math.ceil(soldiersDelta / 5), 8);
          audioSystem.playCascade('soldier_multiply', soundCount, 40);
        }

        // Show gate operation popup
        this.showGateOperationPopup(gate.x, gate.y, operation);

      // Light camera shake for a satisfying gate hit
        const { shakeDuration, shakeIntensity } = EFFECTS.gateActivation;
        this.cameras.main.shake(shakeDuration, shakeIntensity / 1000);
      }
    );

    // Handle weapon box destruction
    events.on('weaponBoxDestroyed', (_box: WeaponBox, weapon: WeaponType) => {
      // Equip the tier printed on this crate. Multiple live crates may offer
      // the same tier, so blindly incrementing here could skip a weapon band.
      const equippedWeapon = this.weaponSystem.equipWeaponPickup(weapon);

      // Update HUD weapon indicator
      this.hud.updateWeapon(equippedWeapon);

      // Play weapon upgrade flourish
      audioSystem.playUpgradeFlourish();

      // Show upgrade popup
      this.showWeaponUpgradePopup(_box.x, _box.y, equippedWeapon);
    });

    // Handle level completion
    events.on('levelComplete', (level: number, tier: number) => {
      this.handleLevelComplete(level, tier);
    });
  }

  /**
   * Handles level completion - transitions to ResultsScene
   */
  private handleLevelComplete(_level: number, _tier: number): void {
    if (this.isResolved) return;
    this.isResolved = true;
    this.failReason = null;

    // Play level complete fanfare
    audioSystem.playLevelCompleteFanfare();

    // Finalize score with army bonus
    const survivingSoldiers = this.player.getSoldierCount();
    const result = scoreSystem.finalizeLevelScore(survivingSoldiers);
    const performance = calculateRunPerformance({
      level: this.currentLevel,
      isVictory: true,
      finaleDefeated: this.spawnSystem.getIsFinaleDefeated(),
      shieldRemaining: this.baseSystem.getHp(),
      shieldMax: this.baseSystem.getMaxHp(),
      leaks: this.baseSystem.getLeaksTaken(),
      shotsFired: this.shotsFired,
      shotsHit: this.shotsHit,
      weapon: this.weaponSystem.getCurrentWeapon(),
    });

    // Track level completion for analytics
    trackLevelComplete(
      this.currentLevel,
      this.currentTier,
      result.breakdown.total,
      survivingSoldiers
    );

    // Prepare results data
    const resultsData: ResultsSceneData = {
      level: this.currentLevel,
      tier: this.currentTier,
      isVictory: true,
      isNewHighScore: result.isNewHighScore,
      enemyKillCount: scoreSystem.getEnemyKillCount(),
      shotsFired: this.shotsFired,
      shotsHit: this.shotsHit,
      accuracy: performance.accuracy,
      grade: performance.grade,
      weapon: this.weaponSystem.getCurrentWeapon(),
      soldiersRemaining: survivingSoldiers,
      baseHpRemaining: this.baseSystem.getHp(),
      baseHpMax: this.baseSystem.getMaxHp(),
      leaks: this.baseSystem.getLeaksTaken(),
      finaleDefeated: this.spawnSystem.getIsFinaleDefeated(),
      breakdown: result.breakdown,
    };

    // Transition to ResultsScene
    this.cameras.main.fadeOut(DURATIONS.screenTransition, 0, 0, 0);
    this.cameras.main.once('camerafadeoutcomplete', () => {
      this.scene.start('ResultsScene', resultsData);
    });
  }

  /**
   * Shows the gate operation popup animation
   */
  private showGateOperationPopup(x: number, y: number, operation: GateOperation): void {
    const popupColor =
      operation.kind === 'sub'
        ? '#FF6B6B'
        : operation.kind === 'add'
          ? '#7EE8FF'
          : '#00FF94';

    const popup = this.add.text(x, y, `${this.formatGateOperation(operation)}!`, {
      fontFamily: FONTS.display,
      fontSize: '44px',
      color: popupColor,
      fontStyle: 'bold',
      stroke: '#000000',
      strokeThickness: 4,
    }).setOrigin(0.5);

    popup.setDepth(60);

    // Float up and fade
    this.tweens.add({
      targets: popup,
      y: y - 72,
      alpha: 0,
      scale: 1.2,
      duration: DURATIONS.multiplierPopup,
      ease: 'Power2',
      onComplete: () => popup.destroy(),
    });
  }

  private formatGateOperation(operation: GateOperation): string {
    if (operation.kind === 'mul') return `×${operation.value}`;
    if (operation.kind === 'add') return `+${operation.value}`;
    return `-${operation.value}`;
  }

  /**
   * Shows the weapon upgrade popup animation
   */
  private showWeaponUpgradePopup(x: number, y: number, weapon: WeaponType): void {
    const popup = this.add.container(x, y);
    popup.setDepth(60);

    const panel = this.add.graphics();
    panel.fillStyle(0x101219, 0.78);
    panel.fillRoundedRect(-62, -44, 124, 88, 14);
    panel.lineStyle(1, 0xffffff, 0.18);
    panel.strokeRoundedRect(-62, -44, 124, 88, 14);

    const icon = this.add.image(0, -8, getWeaponGunTextureKey(weapon)).setOrigin(0.5);
    fitImageContain(icon, 86, 36);

    const label = this.add.text(0, 28, `${getWeaponDisplayName(weapon)}!`, {
      fontFamily: FONTS.display,
      fontSize: '24px',
      color: '#FFD93D',
      fontStyle: 'bold',
      stroke: '#000000',
      strokeThickness: 3,
    }).setOrigin(0.5);
    popup.add([panel, icon, label]);

    // Float up and fade
    this.tweens.add({
      targets: popup,
      y: y - 80,
      alpha: 0,
      scale: 1.3,
      duration: 600,
      ease: 'Power2',
      onComplete: () => popup.destroy(),
    });
  }

  update(_time: number, _delta: number): void {
    // Game loop handled in gameLoop() for cleaner pause management
  }

  /**
   * Main game loop - handles scrolling and game state updates
   */
  private gameLoop(time: number, delta: number): void {
    if (this.isPaused || this.isResolved || (this.deterministicMode && !this.manualStepInProgress)) return;

    // Update parallax lines
    this.updateParallaxLines(delta);

    // Update player input (pointer + keyboard)
    this.inputSystem.update(delta);

    // Update player
    this.player.update(delta);

    // Update spawn system (spawns enemies, gates, weapon boxes based on progress)
    this.spawnSystem.update(delta);
    this.hud.updateProgress(this.spawnSystem.getProgressPercent());

    // Update weapon system (handles shooting and bullet collisions)
    this.weaponSystem.update(time, delta);

    // Update collision system (handles enemy-army collisions)
    this.collisionSystem.update();

    if (this.isResolved) return;

    // Sync soldier count with HUD (in case of damage)
    const currentSoldiers = this.player.getSoldierCount();
    if (currentSoldiers !== this.hud.getSoldierCount()) {
      this.hud.updateSoldierCount(currentSoldiers);
    }

    if (currentSoldiers <= 0) {
      this.handleGameOver('army');
      return;
    }

    // Apply base leak damage when enemies pass the army line
    this.checkEnemyLeaks();
    if (this.isResolved) return;

    // Check for gate collisions with player
    this.checkGateCollisions();
    if (this.isResolved) return;

    // Check for bullet collisions with weapon boxes
    this.checkWeaponBoxCollisions();

    // Update performance monitor (dev only)
    this.performanceMonitor.update(time);
  }

  private checkEnemyLeaks(): void {
    const leakThresholdY = GAME_HEIGHT - 56;
    const enemies = this.spawnSystem.getActiveEnemies();

    for (const enemy of enemies) {
      if (!enemy.getIsAlive() || !enemy.active) continue;
      if (enemy.y <= leakThresholdY) continue;

      // Every leak is charged. Finale targets are mandatory power checks, so a
      // breach consumes the full shield instead of becoming a cheap skip.
      const leakDamage = enemy.getIsFinale() ? this.baseSystem.getHp() : 1;
      enemy.removeFromPlay();
      this.baseSystem.applyLeakDamage(leakDamage);
      this.hud.updateBaseHp(this.baseSystem.getHp());

      if (this.baseSystem.isDepleted()) {
        this.handleGameOver('base');
        return;
      }
    }
  }

  /**
   * Checks for player collisions with gates from spawn system
   */
  private checkGateCollisions(): void {
    const playerX = this.player.getArmyX();
    const playerY = this.player.getArmyY();
    const gates = this.spawnSystem.getActiveGates();

    for (const gate of gates) {
      // Pre-activation highlight based on proximity
      if (!gate.getIsActivated() && gate.getIsActive()) {
        const dx = Math.abs(playerX - gate.x);
        const dy = Math.abs(playerY - gate.y);
        const xWeight = 1 - Phaser.Math.Clamp(dx / (Gate.WIDTH / 2), 0, 1);
        const yWeight = 1 - Phaser.Math.Clamp(dy / 220, 0, 1);
        const intensity = Math.max(0, xWeight * yWeight);
        gate.setApproachIntensity(intensity);
      } else {
        gate.setApproachIntensity(0);
      }

      // Check if player is passing through this gate
      if (!gate.getIsActivated() && gate.getIsActive()) {
        const bounds = gate.getCollisionBounds();

        // Check if player center is within gate bounds horizontally
        // and player has reached gate Y position
        const withinX = playerX >= bounds.x && playerX <= bounds.x + bounds.width;
        const withinY = playerY >= bounds.y && playerY <= bounds.y + bounds.height;

        if (withinX && withinY) {
          // Activate gate and apply operation delta
          const soldiersDelta = gate.activate(this.player.getSoldierCount());
          if (soldiersDelta > 0) {
            this.player.addSoldiers(soldiersDelta, true);
          } else if (soldiersDelta < 0) {
            this.player.removeSoldiers(Math.abs(soldiersDelta));
          }

          if (this.player.isDefeated()) {
            this.handleGameOver('army');
            return;
          }
        }
      }
    }
  }

  /**
   * Checks for bullet collisions with weapon boxes from spawn system
   */
  private checkWeaponBoxCollisions(): void {
    const bullets = this.weaponSystem.getBullets();
    const weaponBoxes = this.spawnSystem.getActiveWeaponBoxes();

    for (const box of weaponBoxes) {
      if (!box.getIsActive()) continue;

      // Check for bullet collisions
      for (const bullet of bullets) {
        if (!bullet.getIsActive()) continue;

        const dx = bullet.x - box.x;
        const dy = bullet.y - box.y;
        const distance = Math.sqrt(dx * dx + dy * dy);
        const collisionDist = bullet.getCollisionRadius() + box.getCollisionRadius();

        if (distance < collisionDist) {
          // Hit the box
          const damage = bullet.getDamage();
          box.takeDamage(damage);
          bullet.deactivate();
          break;
        }
      }
    }
  }

  // ===========================================================================
  // BACKGROUND & TRACK
  // ===========================================================================

  /**
   * Creates the solid background color
   */
  private createBackground(): void {
    createGameplayBackground(this);
  }

  /**
   * Creates the track lane area where the player moves
   */
  private createTrack(): void {
    const playerY = GAME_HEIGHT - SAFE_AREA.bottom - 120;
    createGameplayTrack(this, {
      trackPadding: this.trackPadding,
      laneDividerBottomY: playerY - 60,
      laneDividerAlpha: 0.7,
      laneDividerWidth: 1,
    });
  }

  /**
   * Creates horizontal parallax lines for scrolling effect
   */
  private createParallaxLines(): void {
    this.parallaxLines = createParallaxLines(this, {
      trackPadding: this.trackPadding,
      lineSpacing: EFFECTS.parallax.lineSpacing,
      lineWidth: EFFECTS.parallax.lineWidth,
      lineAlpha: 0.22,
      lineSpeedRatio: EFFECTS.parallax.lineSpeedRatio,
    });
  }

  /**
   * Updates parallax lines position for scrolling effect
   */
  private updateParallaxLines(delta: number): void {
    updateParallaxLines(this.parallaxLines, delta, this.scrollSpeed, {
      lineSpacing: EFFECTS.parallax.lineSpacing,
      lineSpeedRatio: EFFECTS.parallax.lineSpeedRatio,
    });
  }

  // ===========================================================================
  // SCROLL SPEED
  // ===========================================================================

  /**
   * Calculates scroll speed based on current level and tier
   */
  private calculateScrollSpeed(): void {
    const levelMultiplier = SCROLL_SPEED.levelMultipliers[this.currentLevel] || 1.0;
    const tierBonus = (this.currentTier - 1) * SCROLL_SPEED.tierMultiplier;

    this.scrollSpeed = SCROLL_SPEED.base * (levelMultiplier + tierBonus);
  }

  /**
   * Gets the current scroll speed (for other systems to use)
   */
  getScrollSpeed(): number {
    return this.scrollSpeed;
  }

  // ===========================================================================
  // TRACK BOUNDARIES
  // ===========================================================================

  /**
   * Gets the left boundary of the track
   */
  getTrackLeftBound(): number {
    return this.trackLeftBound;
  }

  /**
   * Gets the right boundary of the track
   */
  getTrackRightBound(): number {
    return this.trackRightBound;
  }

  /**
   * Clamps an x position to stay within track bounds
   */
  clampToTrack(x: number, entityWidth: number = 0): number {
    return clampToTrackBounds(x, this.trackLeftBound, this.trackRightBound, entityWidth);
  }

  // ===========================================================================
  // COLLISION GROUPS
  // ===========================================================================

  /**
   * Sets up physics collision groups for entities
   */
  private setupCollisionGroups(): void {
    // Create physics groups that will be used by entities
    // These are created here so they're ready when entities are added

    // Player bullets group - collides with enemies and weapon boxes
    this.physics.add.group({
      classType: Phaser.GameObjects.Sprite,
      runChildUpdate: true,
    });

    // Enemies group - collides with player and bullets
    this.physics.add.group({
      classType: Phaser.GameObjects.Sprite,
      runChildUpdate: true,
    });

    // Gates group - collides with player
    this.physics.add.group({
      classType: Phaser.GameObjects.Sprite,
      runChildUpdate: false,
    });

    // Weapon boxes group - collides with player bullets
    this.physics.add.group({
      classType: Phaser.GameObjects.Sprite,
      runChildUpdate: false,
    });
  }

  // ===========================================================================
  // PAUSE FUNCTIONALITY
  // ===========================================================================

  /**
   * Creates the pause button in the top-right corner
   */
  private createPauseButton(): void {
    this.pauseButton = createPauseIconButton(this, {
      isPaused: () => this.isPaused,
      onToggle: () => this.togglePause(),
    });
    this.updatePauseButtonIcon();
  }

  /**
   * Creates the pause menu overlay (hidden by default)
   */
  private createPauseMenu(): void {
    this.pauseMenu = new PauseMenu(this, {
      onResume: () => this.togglePause(),
      onRestart: () => this.restartLevel(),
      onQuit: () => this.quitToMenu(),
    });
  }

  /**
   * Toggles pause state
   */
  togglePause(): void {
    this.isPaused = !this.isPaused;
    this.updatePauseButtonIcon();

    if (this.isPaused) {
      // Show pause menu
      this.pauseMenu.show();
      // Pause physics
      this.physics.pause();
      // Pause all tweens (except pauseMenu's own tweens)
      this.tweens.pauseAll();
    } else {
      // Hide pause menu
      this.pauseMenu.hide();
      // Resume physics
      this.physics.resume();
      // Resume all tweens
      this.tweens.resumeAll();
    }

  }

  private updatePauseButtonIcon(): void {
    if (!this.pauseButton) return;
    setPauseButtonIcon(this.pauseButton, this.isPaused);
  }

  /**
   * Returns whether the game is currently paused
   */
  getIsPaused(): boolean {
    return this.isPaused;
  }

  /**
   * Restarts the current level
   */
  private restartLevel(): void {
    this.cameras.main.fadeOut(DURATIONS.screenTransition, 0, 0, 0);
    this.cameras.main.once('camerafadeoutcomplete', () => {
      this.scene.restart({ level: this.currentLevel, tier: this.currentTier });
    });
  }

  /**
   * Returns to the main menu
   */
  private quitToMenu(): void {
    this.cameras.main.fadeOut(DURATIONS.screenTransition, 0, 0, 0);
    this.cameras.main.once('camerafadeoutcomplete', () => {
      this.scene.start('TitleScene');
    });
  }

  /**
   * Handles game over when player army is defeated - transitions to ResultsScene
   */
  private handleGameOver(reason: 'army' | 'base' = 'army'): void {
    if (this.isResolved) return;
    this.isResolved = true;
    this.failReason = reason;

    // Track level fail for analytics
    const failPosition = this.spawnSystem?.getProgressPercent() ?? 0;
    const soldiersAtDeath = this.player?.getSoldierCount() ?? 0;
    trackLevelFail(this.currentLevel, this.currentTier, soldiersAtDeath, failPosition, {
      reason,
      baseHp: this.baseSystem?.getHp() ?? 0,
      leaks: this.baseSystem?.getLeaksTaken() ?? 0,
    });

    // Play game over sound sequence
    audioSystem.playGameOverSequence();

    // Get current score breakdown (no army bonus for game over)
    const breakdown = scoreSystem.getBreakdown();
    const performance = calculateRunPerformance({
      level: this.currentLevel,
      isVictory: false,
      finaleDefeated: this.spawnSystem?.getIsFinaleDefeated() ?? false,
      shieldRemaining: this.baseSystem?.getHp() ?? 0,
      shieldMax: this.baseSystem?.getMaxHp() ?? 1,
      leaks: this.baseSystem?.getLeaksTaken() ?? 0,
      shotsFired: this.shotsFired,
      shotsHit: this.shotsHit,
      weapon: this.weaponSystem?.getCurrentWeapon() ?? WeaponType.Pistol,
    });

    // Prepare results data
    const resultsData: ResultsSceneData = {
      level: this.currentLevel,
      tier: this.currentTier,
      isVictory: false,
      failReason: reason,
      baseHpRemaining: this.baseSystem?.getHp() ?? 0,
      baseHpMax: this.baseSystem?.getMaxHp() ?? 0,
      leaks: this.baseSystem?.getLeaksTaken() ?? 0,
      enemyKillCount: scoreSystem.getEnemyKillCount(),
      shotsFired: this.shotsFired,
      shotsHit: this.shotsHit,
      accuracy: performance.accuracy,
      grade: performance.grade,
      weapon: this.weaponSystem?.getCurrentWeapon() ?? WeaponType.Pistol,
      soldiersRemaining: soldiersAtDeath,
      finaleDefeated: this.spawnSystem?.getIsFinaleDefeated() ?? false,
      breakdown: {
        kills: breakdown.kills,
        gateBonus: breakdown.gateBonus,
        armyBonus: 0, // No army bonus on game over
        total: breakdown.total,
      },
    };

    // Transition to ResultsScene
    this.cameras.main.fadeOut(DURATIONS.screenTransition, 0, 0, 0);
    this.cameras.main.once('camerafadeoutcomplete', () => {
      this.scene.start('ResultsScene', resultsData);
    });
  }

  // ===========================================================================
  // AUTOMATION / TEST HOOKS
  // ===========================================================================

  private installAutomationHooks(): void {
    this.deterministicHooks = installDeterministicHooks({
      stepMs: GameScene.DETERMINISTIC_STEP_MS,
      renderToText: this.renderGameToText.bind(this),
      onAdvanceStep: (stepMs) => {
        this.deterministicMode = true;
        this.manualStepInProgress = true;
        this.deterministicTimeMs += stepMs;
        this.gameLoop(this.deterministicTimeMs, stepMs);
        this.manualStepInProgress = false;
      },
    });
  }

  private renderGameToText(): string {
    const enemies = this.spawnSystem?.getActiveEnemies() ?? [];
    const gates = this.spawnSystem?.getActiveGates() ?? [];
    const weaponBoxes = this.spawnSystem?.getActiveWeaponBoxes() ?? [];

    const payload = {
      scene: 'GameScene',
      mode: this.isPaused
        ? 'paused'
        : this.isResolved && this.failReason
          ? 'failed'
          : this.spawnSystem?.getIsLevelComplete()
          ? 'complete'
          : 'playing',
      level: this.currentLevel,
      tier: this.currentTier,
      coords: 'origin top-left, +x right, +y down',
      progress: {
        distance: this.spawnSystem?.getProgressDistance() ?? 0,
        trackLength: this.spawnSystem?.getTrackLength() ?? 0,
        percent: this.spawnSystem?.getProgressPercent() ?? 0,
      },
      player: {
        x: this.player?.getArmyX() ?? 0,
        y: this.player?.getArmyY() ?? 0,
        soldiers: this.player?.getSoldierCount() ?? 0,
        radius: this.player?.getFormationRadius() ?? 0,
      },
      base: {
        hp: this.baseSystem?.getHp() ?? 0,
        maxHp: this.baseSystem?.getMaxHp() ?? 0,
        leaks: this.baseSystem?.getLeaksTaken() ?? 0,
      },
      failReason: this.failReason,
      segment: {
        id: this.spawnSystem?.getCurrentSegmentId() ?? 'onboard',
        armyRatio: this.spawnSystem?.getLastArmyRatio() ?? 1,
        ddaDelta: this.spawnSystem?.getLastDdaDelta() ?? 0,
      },
      weapon: this.weaponSystem?.getCurrentWeapon() ?? WeaponType.Pistol,
      bullets: {
        count: this.weaponSystem?.getBullets().filter((bullet) => bullet.getIsActive()).length ?? 0,
      },
      combat: {
        shotsFired: this.shotsFired,
        shotsHit: this.shotsHit,
        accuracy: this.shotsFired > 0 ? this.shotsHit / this.shotsFired : 0,
        enemiesScheduled: this.spawnSystem?.getTargetEnemyCount() ?? 0,
        enemiesKilled: scoreSystem.getEnemyKillCount(),
        enemyCollisions: this.enemyCollisions,
        finaleDefeated: this.spawnSystem?.getIsFinaleDefeated() ?? false,
      },
      enemies: enemies.map((enemy) => ({
        type: enemy instanceof EliteEnemy ? 'elite' : 'basic',
        isFinale: enemy.getIsFinale(),
        x: enemy.x,
        y: enemy.y,
        hp: enemy.getHp(),
        maxHp: enemy.getMaxHp(),
      })),
      gates: gates.map((gate) => ({
        x: gate.x,
        y: gate.y,
        multiplier: gate.getMultiplier(),
        operation: gate.getOperation(),
        isActive: gate.getIsActive(),
        isActivated: gate.getIsActivated(),
      })),
      weaponBoxes: weaponBoxes.map((box) => ({
        x: box.x,
        y: box.y,
        nextWeapon: box.getNextWeapon(),
        hp: box.getHp(),
        maxHp: box.getMaxHp(),
        isActive: box.getIsActive(),
      })),
    };

    return JSON.stringify(payload);
  }

  // ===========================================================================
  // UTILITIES
  // ===========================================================================

  /**
   * Gets the player army
   */
  getPlayer(): Player {
    return this.player;
  }

  /**
   * Gets all active enemies from the spawn system
   */
  getEnemies(): Enemy[] {
    return this.spawnSystem?.getActiveEnemies() ?? [];
  }

  /**
   * Gets the weapon system
   */
  getWeaponSystem(): WeaponSystem {
    return this.weaponSystem;
  }

  /**
   * Gets the current score
   */
  getScore(): number {
    return scoreSystem.getCurrentScore();
  }

  /**
   * Gets the score breakdown (for results screen)
   */
  getScoreBreakdown() {
    return scoreSystem.getBreakdown();
  }

  /**
   * Adds kill points to the score (prefer using scoreSystem directly)
   */
  addScore(points: number): void {
    scoreSystem.addKillPoints(points);
    this.score = scoreSystem.getCurrentScore();
  }

  /**
   * Cleanup when scene is shutdown
   */
  shutdown(): void {
    if (this.hasShutdown) return;
    this.hasShutdown = true;

    this.events.off('update', this.gameLoop, this);
    this.parallaxLines = [];

    // Destroy particle pool
    destroyParticlePool();

    if (this.inputSystem) {
      this.inputSystem.destroy();
    }

    if (this.weaponSystem) {
      this.weaponSystem.destroy();
    }

    if (this.collisionSystem) {
      this.collisionSystem.destroy();
    }

    if (this.spawnSystem) {
      this.spawnSystem.destroy();
    }

    destroyIfPresent(this.player);
    destroyIfPresent(this.hud);
    destroyIfPresent(this.pauseMenu);
    destroyIfPresent(this.pauseButton);
    destroyIfPresent(this.performanceMonitor);

    uninstallDeterministicHooks(this.deterministicHooks);
    this.deterministicHooks = null;
  }

  /**
   * Gets the input system (for testing)
   */
  getInputSystem(): InputSystem {
    return this.inputSystem;
  }
}
